/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { useMutation } from 'react-query';

import { ClusterApiService } from '../../services/ClusterApiService';
import { EntityId } from '../../types/api/EntityId';
import { ClusterUpdateRequestBody } from '../../types/cluster/ClusterUpdateRequestBody';
import { reactToUpdateCluster } from '../../utils/reactions';

export type UseUpdateClusterAccessor = {
  organizationId: EntityId;
  projectId: EntityId;
  clusterId: EntityId;
};

export type UseUpdateClusterParams = {
  awaitForReaction?: boolean;
  skipReaction?: boolean;
};

export type UseUpdateClusterData = {
  data: ClusterUpdateRequestBody;
};

export type UseUpdateClusterArgs = UseUpdateClusterAccessor & UseUpdateClusterData & UseUpdateClusterParams;

const useUpdateCluster = () => {
  return useMutation(
    ({ organizationId, projectId, clusterId, data }: UseUpdateClusterArgs) => {
      return ClusterApiService.updateCluster({
        organizationId,
        projectId,
        clusterId,
        data,
      });
    },
    {
      onSettled: async (_data, _error, { awaitForReaction, skipReaction, ...accessor }) => {
        if (skipReaction) {
          return;
        }
        awaitForReaction ? await reactToUpdateCluster(accessor) : void reactToUpdateCluster(accessor);
      },
      onError: () => {
        // place error handler here
      },
      throwOnError: false,
    },
  );
};

export { useUpdateCluster };
